/**
 * User Initialization Hook - Complete Authentication & Organization Setup
 *
 * Manages the entire user initialization lifecycle in a single, cohesive hook.
 * Combines authentication (MSAL or Kinde), user context loading, and organization
 * setup with internal state management and error handling.
 *
 * Replaces the complex orchestration pattern in UserRequiredContainer
 * with a clean, performant, single-responsibility hook.
 *
 * @module useUserInitialization
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { selectOrganisation } from '@/app/slices/organisationSlice'
import { getLanguageToUse, getLocaleToUse, initUserFlow, Paths, RESELLERS } from '@/common/utils'
import { appInsights } from '@/common/utils/ApplicationInsights/ApplicationInsights'
import { handleAuthenticationError } from '@/common/utils/AuthErrors'
import { determineRedirectPath } from '@/common/utils/subscriptionUtils'

import { useAuthenticationFlow } from './useAuthenticationFlow'
import { useChurnkeyLoader } from './useChurnkeyLoader'
import { useChurnkeyWalls } from './useChurnkeyWalls'
import { useIntercomMessenger } from './useIntercomMessenger'
import useLoadingContext from './useLoadingContext'
import { useManufacturingDataLoader } from './useManufacturingDataLoader'
import { useOrganisationLoader } from './useOrganisationLoader'

/**
 * Complete user initialization hook with internal state management.
 *
 * Features:
 * - Single stable function reference (no recreation on render)
 * - Internal progress tracking (no external ref needed)
 * - Comprehensive error handling with typed errors
 * - Analytics tracking integration
 * - Automatic redirect logic
 * - Performance optimized with minimal dependencies
 *
 * @param {Object} config - Initialization configuration
 * @param {Object} config.currentUser - Current user from Redux state
 * @param {Object} config.planMetadata - Plan metadata from Redux state
 * @param {Object} config.location - Router location object
 * @param {Function} config.navigate - Router navigate function
 * @param {Function} config.trackEvent - Analytics tracking function
 * @returns {{
 *   steps: Object,
 *   currentStep: number,
 *   isComplete: boolean,
 *   hasErrors: boolean,
 *   isAnyStepLoading: boolean,
 *   hasDataErrors: boolean,
 *   initializeApp: Function
 * }} Initialization state and controls:
 * - steps: Individual step states from loading context
 * - currentStep: Currently active step index
 * - isComplete: Whether all steps are complete
 * - hasErrors: Whether any step has errors
 * - isAnyStepLoading: Whether any step is currently loading
 * - hasDataErrors: Whether any step has data errors
 * - initializeApp: Stable initialization function that returns {success: boolean, reason: string, error?: Error}
 */
export const useAppInitialisation = ({ currentUser, location, navigate, planMetadata, trackEvent }) => {
    const { initializeAuthentication } = useAuthenticationFlow()
    const { loadOrganisationContext } = useOrganisationLoader()
    const { loadManufacturingData } = useManufacturingDataLoader()
    const { bootIntercomMessenger } = useIntercomMessenger()
    const { loadChurnkey } = useChurnkeyLoader()
    const { runChurnkeyWalls } = useChurnkeyWalls()

    const { currentStep, hasErrors, isComplete, setStepError, setStepErrorMessage, steps } = useLoadingContext()

    // Internal progress tracking - no external ref needed
    const initializationInProgressRef = useRef(false)

    // Re-run Churnkey walls whenever the active organisation changes.
    // The ref keeps `runChurnkeyWalls` out of the effect deps so the effect
    // fires only when customerId / firstSubscriptionId actually change,
    // not on every render caused by an unstable callback identity.
    const organisation = useSelector(selectOrganisation)
    const customerId = organisation?.paymentGatewayCustomerId
    const firstSubscriptionId = organisation?.subscriptions?.[0]?.paymentGatewaySubscriptionId

    const runChurnkeyWallsRef = useRef(runChurnkeyWalls)
    useEffect(() => {
        runChurnkeyWallsRef.current = runChurnkeyWalls
    })

    useEffect(() => {
        if (!customerId) return undefined
        const fire = () => runChurnkeyWallsRef.current({ customerId, firstSubscriptionId })
        if (typeof requestIdleCallback === 'function') {
            const id = requestIdleCallback(fire)
            return () => {
                if (typeof cancelIdleCallback === 'function') cancelIdleCallback(id)
            }
        }
        const id = setTimeout(fire, 0)
        return () => clearTimeout(id)
    }, [customerId, firstSubscriptionId])

    /**
     * Complete user initialization sequence.
     *
     * This function has a stable reference and won't cause re-renders.
     * All complex orchestration is handled internally.
     */
    const initializeApp = useCallback(async () => {
        try {
            console.debug('[UserInitialization] Starting initialization, currentUser:', currentUser ? 'exists' : 'none')

            // Early return guards
            if (currentUser && Object.keys(currentUser).length > 0) {
                console.debug('[UserInitialization] User already loaded, skipping initialization')
                return { success: true, reason: 'already_initialized' }
            }

            if (initializationInProgressRef.current) {
                console.debug('[UserInitialization] Initialization already in progress, skipping')
                return { success: false, reason: 'in_progress' }
            }

            initializationInProgressRef.current = true

            // Step 1: Authentication Flow
            console.debug('[UserInitialization] Starting authentication flow')

            const { provider, userContext } = await initializeAuthentication()

            // Early check: New users without organization should be redirected to welcome
            if (!userContext.selectedOrganisationId) {
                console.debug('[UserInitialization] New user without organization, redirecting to welcome')

                // Track analytics for new user redirect
                trackEvent({
                    eventName: 'New user redirected to welcome',
                    data: {
                        currentUser: userContext.user,
                        reason: 'no_organization',
                        userId: userContext.user.userId,
                        provider,
                    },
                })

                // Clean up initialization state
                initializationInProgressRef.current = false

                // Navigate to welcome page
                navigate(Paths.WELCOME_PATHNAME, { replace: true })

                return { success: true, reason: 'redirected_to_welcome' }
            }

            // Step 2: Organization Loading
            console.debug('[UserInitialization] Starting organization loading')

            const { organisation } = await loadOrganisationContext(
                userContext.user,
                userContext.selectedOrganisationId,
                userContext
            )

            // Set App Insights user context for error correlation
            // User ID format: B2C UUID or Kinde 'kp_xxx'
            if (appInsights) {
                appInsights.setAuthenticatedUserContext(
                    userContext.user.userId,
                    organisation?.organisationId,
                    true // Store in cookies for session correlation
                )
                console.debug('[UserInitialization] App Insights user context set:', userContext.user.userId)
            }

            // Fire-and-forget: Load manufacturing data in background
            // RTK Query will cache the data when it arrives, no need to block the app
            loadManufacturingData(userContext.selectedOrganisationId)

            // Step 4: Application Setup
            console.debug('[UserInitialization] Completing application setup')

            // Initialize Intercom + Churnkey SDK (deferred to avoid blocking critical path).
            // Wall checks are handled by a separate effect keyed on the active
            // organisation so they re-fire on org switch, not only on init.
            const bootDeferredScripts = () => {
                bootIntercomMessenger(organisation, userContext)
                loadChurnkey()
            }
            if (typeof requestIdleCallback === 'function') {
                requestIdleCallback(bootDeferredScripts)
            } else {
                setTimeout(bootDeferredScripts, 0)
            }

            // Initialize user flow
            initUserFlow({
                user: userContext.user,
                organisation,
                localeToUse: getLocaleToUse(organisation),
                language: getLanguageToUse(userContext.user, organisation),
                planMetadata,
                reseller: import.meta.env.VITE_RESELLER ? RESELLERS[import.meta.env.VITE_RESELLER].name : undefined,
            })

            // Step 5: Redirect Logic
            const redirectTo = determineRedirectPath(organisation, location.pathname)

            if (redirectTo) {
                trackEvent({
                    eventName: `User redirected to ${redirectTo}`,
                    data: { currentUser: userContext.user, organisation, provider },
                })
                navigate(redirectTo)
            } else {
                trackEvent({
                    eventName: 'User not redirected',
                    data: { currentUser: userContext.user, organisation, provider },
                })
            }

            console.debug('[UserInitialization] Initialization completed successfully')

            initializationInProgressRef.current = false

            return { success: true, reason: 'completed' }
        } catch (error) {
            console.error('[UserInitialization] Initialization failed:', error)
            initializationInProgressRef.current = false

            // Use typed error handling system
            handleAuthenticationError(error, setStepError, setStepErrorMessage, (authError) => {
                trackEvent({
                    eventName: 'Application initialization failed',
                    data: {
                        errorCode: authError.code,
                        errorMessage: authError.message,
                        loadingStep: authError.loadingStep,
                        recoverable: authError.recoverable,
                    },
                })
            })

            return { success: false, reason: 'error', error }
        }
    }, [
        currentUser,
        planMetadata,
        location.pathname,
        navigate,
        trackEvent,
        initializeAuthentication,
        loadOrganisationContext,
        loadManufacturingData,
        bootIntercomMessenger,
        loadChurnkey,
        setStepError,
        setStepErrorMessage,
    ])

    // Memoized aggregated states from context
    const stepEntries = useMemo(() => Object.entries(steps), [steps])
    const isAnyStepLoading = useMemo(() => stepEntries.some(([, step]) => step.isLoading), [stepEntries])
    const hasDataErrors = useMemo(() => stepEntries.some(([, step]) => !!step.error), [stepEntries])

    return {
        // Global loading state from context
        steps,
        currentStep,
        isComplete,
        hasErrors,

        // Aggregated states from context
        isAnyStepLoading,
        hasDataErrors,

        // Stable initialization function
        initializeApp,
    }
}
